import { FC, useRef, useState } from 'react';
import { ProgressStage } from './ApplicationProgressBox';
import { cn } from 'lib/utils';
import { statusConfig } from './progressBoxData';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import TickBox from './TickBox';
import ProgressBar from './ProgressBar';
import BPButton from 'components/BionicUI/button/BPButton';
import { UserRoles } from 'core/models/users/user.model';

const ProgressDisplay: FC<{
  stage: ProgressStage;
  userRoles: UserRoles | undefined;
  onClick: () => void;
}> = ({ stage, onClick, userRoles }) => {
  const title =
    userRoles && userRoles.includes('PROJECT_OWNER')
      ? ['Your Application', 'Complete Project Onboarding', 'Start Fundraising']
      : ['Your Application', 'Start Fundraising'];
  const stageNumRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const continueRef = useRef<HTMLDivElement>(null);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [pulseMessage, setPulseMessage] = useState<boolean>(false);

  useGSAP(() => {
    gsap.set([stageNumRef.current, titleRef.current, messageRef.current], {
      alpha: 0,
    });
    gsap.set(messageRef.current, { y: -10 });
    gsap.set(continueRef.current, { alpha: 0, x: -20 });

    gsap
      .timeline({ delay: 0.4 + 0.3 * stage.id })
      .call(() => setIsChecked(true))
      .to(stageNumRef.current, {
        alpha: 1,
        duration: 0.15,
        ease: 'power3.out',
      })
      .to(
        titleRef.current,
        {
          alpha: 1,
          duration: 0.3,
          ease: 'power3.out',
        },
        '-=0.1'
      )
      .to(
        messageRef.current,
        {
          alpha: 1,
          y: 0,
          duration: 0.5,
          ease: 'power3.inout',
          onComplete: () => setPulseMessage(true),
        },
        '-=0.4'
      )
      .to(
        continueRef.current,
        {
          alpha: 1,
          x: 0,
          duration: 0.5,
          delay: 0.5,
          ease: 'power3.inout',
        },
        '<0.1'
      );
  }, []);

  return (
    <>
      <div className="grid grid-rows-[40px_1fr] sm:grid-cols-[40px_1fr] gap-3 sm:items-center">
        <TickBox isChecked={isChecked} status={stage.status} />
        <ProgressBar status={stage.status} stageId={stage.id} />
      </div>
      <div className="grid relative grid-flow-col grid-rows-[auto_auto_auto_auto] gap-x-4 gap-y-1 w-full h-fit">
        <div
          ref={stageNumRef}
          className="h-[40px] sm:h-fit flex items-center text-[16px] text-bionic-muted sm:pt-4"
        >
          0{stage.id}
        </div>
        <div ref={titleRef} className="text-base font-bold sm:text-[18px]">
          {title[stage.id - 1]}
        </div>
        <div
          ref={messageRef}
          className={cn(
            'text-xs sm:text-[14px] text-bionic-muted mt-5',
            stage.status === 'in-progress' && pulseMessage
              ? 'animate-pulse'
              : '',
            stage.status === 'complete' || stage.status === 'submitted'
              ? `rounded-[999px] sm:px-6 px-4 py-1 sm:py-[5px] w-fit h-fit backdrop-blur-md border-[1px] border-solid ${statusConfig[stage.status].border} ${statusConfig[stage.status].bg} `
              : '',
            statusConfig[stage.status].text
          )}
        >
          {statusConfig[stage.status].message}
          {stage?.remarks ? (
            <p className="text-bionic-muted text-sm mt-2">{stage?.remarks}</p>
          ) : null}
        </div>

        <div ref={continueRef} className="mt-4">
          {stage?.route &&
          stage.status !== 'in-progress' &&
          stage?.buttonText !== undefined ? (
            <BPButton onClick={onClick}>
              {stage.buttonText ? stage.buttonText : 'Continue'}
            </BPButton>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default ProgressDisplay;
